import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Icon } from '@iconify/react';
import { Button, Chip, Spinner, useDisclosure } from '@nextui-org/react';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { DEFAULT_DASHBOARD_TABS } from '../../constants/default-tabs';
import {
  useCreateTenantConfig,
  usePatchTenantConfig,
  useTagSpecs,
  useTenantConfig,
  useUpdateTagSpec,
} from '../../hooks/queries/tenant-config';
import { DashboardTab } from '../../services/tenant-config-service';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { SortableTabItem } from './components/sortable-tab-item';
import TabModal from './components/tab-modal';

interface TabFormData {
  id?: string;
  label: string;
  tag: string;
  is_active: boolean;
}

export default function TabsPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { tenantId } = useActiveTenant();

  const {
    data: tenantConfig,
    isLoading,
    refetch: refetchConfig,
  } = useTenantConfig(tenantId!);
  const createConfig = useCreateTenantConfig();
  const patchConfig = usePatchTenantConfig(tenantConfig?.id || 0);
  const updateTagSpec = useUpdateTagSpec();
  const { data: tagSpecs } = useTagSpecs();

  const {
    isOpen: isTabModalOpen,
    onOpen: onTabModalOpen,
    onClose: onTabModalClose,
  } = useDisclosure();

  const [formData, setFormData] = useState<TabFormData>({
    id: '',
    label: '',
    tag: '',
    is_active: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const tabs = tenantConfig?.dashboard_config?.tabs || [];
  const [items, setItems] = useState<DashboardTab[]>([]);

  useEffect(() => {
    if (tabs?.length === 0) {
      // Use default tabs if no custom tabs exist
      const defaultTabs = DEFAULT_DASHBOARD_TABS.map((tab) => ({
        ...tab,
        id: `default-${tab.priority}`,
      }));
      setItems(defaultTabs);
    } else {
      // Use existing custom tabs
      setItems(tabs || []);
    }
  }, [tabs.length]);

  // Helper function to normalize priorities (ensure sequential 1, 2, 3...)
  const normalizePriorities = (tabItems: DashboardTab[]): DashboardTab[] => {
    return tabItems
      .sort((a, b) => a.priority - b.priority)
      .map((item, index) => ({
        ...item,
        priority: index + 1,
      }));
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        if (!items || !Array.isArray(items)) return [];

        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex).map(
          (item, idx) => ({
            ...item,
            priority: idx + 1,
          })
        );

        return newItems;
      });
    }
  };

  const handleSubmitTab = () => {
    if (!formData.label.trim() || !formData.tag.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.id) {
      // Edit existing tab
      setItems((currentItems) =>
        (currentItems || []).map((item) =>
          item.id === formData.id
            ? {
                ...item,
                label: formData.label,
                tag: formData.tag,
                is_active: formData.is_active,
              }
            : item
        )
      );
    } else {
      // Add new tab with safe priority calculation
      setItems((currentItems) => {
        const safeCurrentItems = currentItems || [];

        // Calculate the next priority by finding the highest existing priority
        const maxPriority =
          safeCurrentItems.length > 0
            ? Math.max(...safeCurrentItems.map((item) => item.priority || 0))
            : 0;

        const newTab: DashboardTab = {
          id: Date.now().toString(),
          label: formData.label,
          tag: formData.tag,
          is_active: formData.is_active,
          priority: maxPriority + 1,
        };

        return [...safeCurrentItems, newTab];
      });
    }

    setFormData({ label: '', tag: '', is_active: true });
    onTabModalClose();
  };

  const handleDeleteTab = (id: string) => {
    setItems((currentItems) => {
      if (!currentItems || !Array.isArray(currentItems)) return [];

      // Filter out the deleted tab
      const filteredItems = currentItems.filter((item) => item.id !== id);

      // Normalize priorities to ensure sequential ordering
      return normalizePriorities(filteredItems);
    });
  };

  const handleToggleTab = async (id: string) => {
    // Update local state immediately for better UX
    setItems((currentItems) =>
      (currentItems || []).map((item) =>
        item.id === id ? { ...item, is_active: !item.is_active } : item
      )
    );

    try {
      // Create updated items with toggled status
      const updatedItems = items.map((item) =>
        item.id === id ? { ...item, is_active: !item.is_active } : item
      );

      // Normalize priorities before saving
      const normalizedUpdatedItems = normalizePriorities(updatedItems);

      // Update the configuration via API
      if (tenantConfig?.id) {
        await patchConfig.mutateAsync({
          dashboard_config: {
            tabs: normalizedUpdatedItems,
          },
        });
      }
    } catch (error) {
      console.error('Error updating tab status:', error);
      // Revert the local state change on error
      setItems((currentItems) =>
        (currentItems || []).map((item) =>
          item.id === id ? { ...item, is_active: !item.is_active } : item
        )
      );
      toast.error('Failed to update tab status');
    }
  };

  const handleEdit = (tab: DashboardTab) => {
    setFormData({
      id: tab.id,
      label: tab.label,
      tag: tab.tag,
      is_active: tab.is_active,
    });
    onTabModalOpen();
  };

  const handleSave = async () => {
    // TODO: This should be handled atomically in the backend with database transactions
    // to ensure data consistency. The current client-side rollback is a temporary solution.
    let originalTagSpec: any = null;
    let tagSpecUpdated = false;

    try {
      // Normalize priorities before saving to ensure consistency
      const normalizedItems = normalizePriorities(items || []);

      // Store original tag spec for potential rollback
      const dashboardTagSpec = tagSpecs?.results[0];
      if (dashboardTagSpec) {
        originalTagSpec = { ...dashboardTagSpec };
      }

      // First, create any new tags that don't exist yet
      if (dashboardTagSpec && normalizedItems.length > 0) {
        const existingTagValues =
          dashboardTagSpec.constraints.allowed_values?.map(
            (tag: { value: string }) => tag.value.toLowerCase()
          ) || [];

        const newTagValues = normalizedItems
          .map((item) => item.tag)
          .filter((tag) => !existingTagValues.includes(tag.toLowerCase()))
          .filter((tag, index, arr) => arr.indexOf(tag) === index); // Remove duplicates

        if (newTagValues.length > 0) {
          const updatedConstraints = {
            ...dashboardTagSpec.constraints,
            allowed_values: [
              ...dashboardTagSpec.constraints.allowed_values,
              ...newTagValues.map((tagValue) => ({
                value: tagValue,
                label: tagValue,
                is_active: true,
              })),
            ],
          };

          await updateTagSpec.mutateAsync({
            id: dashboardTagSpec.id!,
            data: {
              constraints: updatedConstraints,
            },
          });
          tagSpecUpdated = true;
        }
      }

      // If we're saving default tabs unchanged, save an empty array to use defaults
      const tabsToSave =
        tabs.length === 0 &&
        JSON.stringify(normalizedItems) ===
          JSON.stringify(
            DEFAULT_DASHBOARD_TABS.map((tab) => ({
              ...tab,
              // id: Date.now().toString(),
            }))
          )
          ? []
          : normalizedItems;

      // Save the tenant configuration
      if (tenantConfig?.id) {
        // Update existing configuration
        await patchConfig.mutateAsync({
          agent_first_message:
            tenantConfig?.agent_first_message ||
            'Welcome to Mentora LiFT! Are you ready to get started?',
          dashboard_config: { tabs: tabsToSave },
        });
      } else {
        // Create new configuration
        await createConfig.mutateAsync({
          // tenant: { id: tenantId! },
          agent_first_message:
            tenantConfig?.agent_first_message ||
            'Welcome to Mentora LiFT! Are you ready to get started?',
          dashboard_config: { tabs: tabsToSave },
        });
      }
      await refetchConfig();

      // If we reach here, everything succeeded
      navigate(`/${tenantSlug}/tenant-config`);
    } catch (error: any) {
      console.error('Error saving tabs:', error);

      // Rollback tag spec changes if they were made but tenant config failed
      // This is a client-side workaround - ideally this should be handled atomically in the backend
      if (tagSpecUpdated && originalTagSpec) {
        try {
          await updateTagSpec.mutateAsync({
            id: originalTagSpec.id!,
            data: {
              constraints: originalTagSpec.constraints,
            },
          });
          console.log('Successfully rolled back tag spec changes');
        } catch (rollbackError) {
          console.error('Failed to rollback tag spec changes:', rollbackError);
          toast.error(
            'Configuration failed and rollback was unsuccessful. Please refresh the page.'
          );
          return;
        }
      }

      // Show appropriate error message
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.dashboard_config?.[0] ||
        'Failed to save configuration';
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-8">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm mb-8">
        <button
          onClick={() => navigate(`/${tenantSlug}/tenant-config`)}
          className="text-default-600 hover:text-default-900"
        >
          Tenant Config
        </button>
        <Icon
          icon="heroicons:chevron-right"
          className="w-4 h-4 text-default-400"
        />
        <span className="font-medium">Tabs Configuration</span>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-lg font-semibold text-foreground mb-1">
            Configure Dashboard Tabs
          </h1>
          <p className="text-md text-default-600">
            Add, remove, and reorder tabs for your dashboard
          </p>
        </div>
        <div className="flex gap-3 ">
          <Button
            color="primary"
            startContent={<Icon icon="heroicons:check" width={16} />}
            onClick={handleSave}
            isLoading={patchConfig.isPending || createConfig.isPending}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Centered Tabs Section */}
      <div className="max-w-3xl mx-auto bg-content1 rounded-large shadow-small p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Icon icon="heroicons:squares-2x2" className="w-5 h-5" />
            <span className="font-medium">
              {items.length} {items.length === 1 ? 'Tab' : 'Tabs'}
            </span>
            {tabs?.length === 0 && (
              <Chip size="sm" variant="flat" color="warning">
                Default Configuration
              </Chip>
            )}
            {tabs.length > 0 && (
              <Chip size="sm" variant="flat" color="success">
                Custom Configuration
              </Chip>
            )}
          </div>
          <Button
            color="secondary"
            variant="flat"
            startContent={<Icon icon="heroicons:plus" width={16} />}
            onClick={onTabModalOpen}
          >
            Add Tab
          </Button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {items.map((tab) => (
                <SortableTabItem
                  key={tab.id}
                  tab={tab}
                  onDelete={() => handleDeleteTab(tab.id)}
                  onToggle={() => handleToggleTab(tab.id)}
                  onEdit={() => handleEdit(tab)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {items.length === 0 && (
          <div className="text-center py-12">
            <Icon
              icon="heroicons:squares-plus"
              className="mx-auto text-default-400 mb-3"
              width={40}
            />
            <p className="text-sm text-default-600 mb-3">
              No tabs configured. Click "Add Tab" to get started.
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <TabModal
        isOpen={isTabModalOpen}
        onClose={onTabModalClose}
        onSubmit={handleSubmitTab}
        formData={formData}
        setFormData={setFormData}
        items={items}
      />
    </div>
  );
}
